import { EthAddress } from '@xylabs/sdk-js'
import { TypedEvent, XyoWorldMinterVersion1 } from '@xyo-network/world-typechain'

import { Quadkey } from '../../../Quadkey'
import { MinterDeployBlocks } from '../DeployBlocks'

export class EventsV1 {
  public bidder?: EthAddress

  public minter?: XyoWorldMinterVersion1

  public quadkey?: Quadkey

  constructor(minter?: XyoWorldMinterVersion1, filter?: { bidder?: EthAddress; quadkey?: Quadkey }) {
    this.quadkey = filter?.quadkey
    this.bidder = filter?.bidder
    this.minter = minter
  }

  public async all(fromBlockOrBlockHash?: number | string, toBlock?: number) {
    const promises: Promise<TypedEvent[]>[] = []
    promises.push(this.auctionStarted(fromBlockOrBlockHash, toBlock))
    promises.push(this.bidPlaced(fromBlockOrBlockHash, toBlock))
    promises.push(this.refundedCurrentBid(fromBlockOrBlockHash, toBlock))
    promises.push(this.tokensBurned(fromBlockOrBlockHash, toBlock))
    promises.push(this.tokensFlowed(fromBlockOrBlockHash, toBlock))
    promises.push(this.geotokenMinted(fromBlockOrBlockHash, toBlock))

    const results = await Promise.allSettled(promises)
    const events = results.map((result) => {
      if (result.status === 'fulfilled') {
        return result.value
      } else {
        console.error(`v3.all: ${JSON.stringify(result.reason)}`)
        return []
      }
    })

    return events.flat().sort((n1, n2) => {
      if (n1.blockNumber < n2.blockNumber) {
        return -1
      }
      if (n1.blockNumber > n2.blockNumber) {
        return 1
      }
      return 0
    })
  }

  public async auctionStarted(fromBlockOrBlockHash: number | string = MinterDeployBlocks.v1, toBlock?: number) {
    const { minter } = this
    return minter
      ? await minter.queryFilter(minter.filters.AuctionStarted(this.quadkey?.toHex() ?? null, null, null), fromBlockOrBlockHash, toBlock)
      : []
  }

  // we needed to have special code here to deal with the fact that v1 of the contract did not have the id indexed
  public async bidPlaced(fromBlockOrBlockHash: number | string = MinterDeployBlocks.v1, toBlock?: number) {
    const { minter } = this
    return minter
      ? (await minter.queryFilter(minter.filters.BidPlaced(null, this.quadkey?.toHex() ?? null, null), fromBlockOrBlockHash, toBlock)).filter(
          (event) => event !== undefined,
        )
      : []
  }

  public async geotokenMinted(fromBlockOrBlockHash: number | string = MinterDeployBlocks.v1, toBlock?: number) {
    const { minter } = this
    return minter ? await minter.queryFilter(minter.filters.GeotokenMinted(null, this.quadkey?.toHex() ?? null), fromBlockOrBlockHash, toBlock) : []
  }

  public async refundedCurrentBid(fromBlockOrBlockHash: number | string = MinterDeployBlocks.v1, toBlock?: number) {
    const { minter } = this
    return minter
      ? await minter.queryFilter(minter.filters.RefundedCurrentBid(null, this.quadkey?.toHex() ?? null, null), fromBlockOrBlockHash, toBlock)
      : []
  }

  public async tokensBurned(fromBlockOrBlockHash: number | string = MinterDeployBlocks.v1, toBlock?: number) {
    const { minter } = this
    return minter ? await minter.queryFilter(minter.filters.TokensBurned(this.quadkey?.toHex() ?? null, null), fromBlockOrBlockHash, toBlock) : []
  }

  public async tokensFlowed(fromBlockOrBlockHash: number | string = MinterDeployBlocks.v1, toBlock?: number) {
    const { minter } = this
    if (minter) {
      const from = await minter.queryFilter(minter.filters.TokensFlowed(null, this.quadkey?.toHex() ?? null, null), fromBlockOrBlockHash, toBlock)
      const to = await minter.queryFilter(minter.filters.TokensFlowed(this.quadkey?.toHex() ?? null, null, null), fromBlockOrBlockHash, toBlock)
      return from.concat(to)
    }
    return []
  }
}
